import {
  createContext,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { StorageScope } from '@services/api/apiConfig';

interface StorageScopeContextValue {
  scope: StorageScope | null;
  setScope: (scope: StorageScope | null) => void;
}

const StorageScopeContext = createContext<StorageScopeContextValue | undefined>(undefined);

/**
 * In-memory authority for the currently selected storage.
 *
 * localStorage remains a restoration hint only. Network operations receive a
 * captured copy of this pair so a later tab or UI switch cannot retarget work
 * that has already started.
 */
export function StorageScopeProvider({ children }: { children: ReactNode }) {
  const [scope, setScopeState] = useState<StorageScope | null>(null);
  const setScope = useCallback((next: StorageScope | null) => {
    setScopeState(next ? { ...next } : null);
  }, []);
  const value = useMemo(() => ({ scope, setScope }), [scope, setScope]);

  return (
    <StorageScopeContext.Provider value={value}>
      {children}
    </StorageScopeContext.Provider>
  );
}

export { StorageScopeContext };
